import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";

interface CashFlow {
  id: string;
  date: string;
  received_amount: string;
  total_expense: string;
  received_amount_without_currency: string;
  total_expense_without_currency: string;
}
interface CashFlowWithFooter extends CashFlow {
  isFooter?: boolean;
}
export default function CashFlowReport() {
  const [filteredItems, setFilteredItems] = useState<CashFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { currency } = useCurrency();
  // console.log(currency);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;

    // Ensure local YYYY-MM-DD format
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2); // Month is 0-indexed
    const day = `0${date.getDate()}`.slice(-2);

    return `${year}-${month}-${day}`;
  };

  const [sortColumn, setSortColumn] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchData = async (page = 1, perPage = 10, sortField = sortColumn, sortDir = sortOrder) => {
    setLoading(true);
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);

      const response = await axios.get(
        `${baseUrl}call_history/getDailyFinancialReport`,
        {
          params: {
            page,
            per_page: perPage,
            from_date: formattedFromDate,
            to_date: formattedToDate,
            sort_by: sortField,
            sort_order: sortDir,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data: CashFlow[] = response.data.data;
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, fromDate, toDate]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, sortColumn, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, sortColumn, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "date";

    setSortColumn(sortField);
    setSortOrder(sortDirection);
    setCurrentPage(1);
    fetchData(1, perPage, sortField, sortDirection);
  };

  const handleFullReset = () => {
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  const getCashFlowColumns = () => {
    return [
      {
        name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
        cell: (row: CashFlowWithFooter, index: number) =>
          row.isFooter ? "" : (currentPage - 1) * perPage + index + 1,
        width: "80px",
      },
      {
        name: <div style={{ whiteSpace: "normal" }}>Date</div>,
        selector: (row: CashFlow) => row.date,
        sortable: true,
        sortField: "date",
        cell: (row: CashFlowWithFooter) =>
          row.isFooter ? <strong>Total</strong> : row.date,
      },
      {
        name: (
          <div style={{ whiteSpace: "normal" }}>
            Amount Received {currency ? `(${currency})` : ""}
          </div>
        ),
        selector: (row: CashFlow) => row.received_amount_without_currency,
        sortable: true,
        sortField: "received_amount",
        cell: (row: CashFlowWithFooter) =>
          row.isFooter ? (
            <strong>
              {currency} {totalAmount.toLocaleString()}
            </strong>
          ) : (
            row.received_amount_without_currency
          ),
      },
      {
        name: (
          <div style={{ whiteSpace: "normal" }}>
            Total Expense {currency ? `(${currency})` : ""}
          </div>
        ),
        selector: (row: CashFlow) => row.total_expense_without_currency,
        sortable: true,
        sortField: "total_expense",
        cell: (row: CashFlowWithFooter) =>
          row.isFooter ? (
            <strong>
              {currency} {totalAgentAmount.toLocaleString()}
            </strong>
          ) : (
            row.total_expense_without_currency || "N/A"
          ),
      },
    ];
  };

  const calculateTotals = () => {
    const totalAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row.received_amount_without_currency || "0").replace(/,/g, "")
      );
      return acc + amount;
    }, 0);

    const totalAgentAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row.total_expense_without_currency || "0").replace(/,/g, "")
      );
      return acc + amount;
    }, 0);

    return {
      totalAmount,
      totalAgentAmount,
    };
  };
  const { totalAmount, totalAgentAmount } = calculateTotals();

  const columnsData: CashFlowWithFooter[] = [
    ...filteredItems,
    {
      isFooter: true,
      id: "",
      date: "Total",
      received_amount: "",
      total_expense: "",
      received_amount_without_currency: "",
      total_expense_without_currency: "",
    },
  ];

  const fetchAllDataWithoutPagination = async () => {
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      const response = await axios.get(
        `${baseUrl}call_history/getDailyFinancialReportNoPagination`,
        {
          params: {
            from_date: formattedFromDate,
            to_date: formattedToDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(response.data, "data"); // Use response.data directly
      return response.data; // Return the response directly
    } catch (error) {
      console.error("Error fetching all data without pagination:", error);
      return []; // Return an empty array on error
    }
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row: any) => row.isFooter === true,
      style: {
        fontWeight: "800",
        backgroundColor: "#f8f9fa",
        color: "#308e87",
        borderTop: "2px solid #dee2e6",
        fontSize: "16px",
      },
    },
  ];

  const exportPDF = async () => {
    const allData = await fetchAllDataWithoutPagination();
    const tableData = allData.map((row: any, index: number) => [
      index + 1,
      row.date || "N/A",
      row.received_amount_without_currency || "0",
      row.total_expense_without_currency || "0",
    ]);

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currencyMap[currency] || currency;

    const footerRow = [
      "",
      "Total",
      `${totalAmount}`,
      `${totalAgentAmount}`,
    ];

    exportToPDF({
      fileName: "Cash_Flow_Report",
      columns: [
        "S.No",
        "Date",
        `Received ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
        `Expense ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
      ],
      data: tableData,
      emptyDataMessage: "No cash flow available for export.",
      footerRow,
      autoTableOptions: {
        headStyles: { fillColor: [0, 105, 92] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { halign: "center" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
        didParseCell: (data: any) => {
          if (data.row.index === tableData.length) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [245, 245, 245];
          }
        },
      },
    });
  };

  const exportExcel = async () => {
    const allData = await fetchAllDataWithoutPagination();
    if (!allData.length) {
      toast.warn("No cash flow available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const excelCurrency = currencyMap[currency] || currency;
    const receivedHeader = `Amount Received ${excelCurrency ? `(${excelCurrency})` : ""}`;
    const expenseHeader = `Total Expense ${excelCurrency ? `(${excelCurrency})` : ""}`;

    const tableData = allData.map((row: CashFlow, index: number) => {
      return {
        "S.No": index + 1,
        Date: row.date || "N/A",
        [receivedHeader]: row.received_amount_without_currency || "0",
        [expenseHeader]: row.total_expense_without_currency || "0",
      };
    });

    const footerRow = {
      "S.No": "",
      Date: "Total",
      [receivedHeader]: totalAmount,
      [expenseHeader]: totalAgentAmount,
    };

    excelExportUtils(tableData, "Cash_Flow_Report", "Cash Flow", footerRow);
  };

  return (
    <>
      <Breadcrumbs
        pageTitle="Cash Flow Report"
        parent="Reports"
        title="Cash Flow Report"
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-end">
              <Col xl={2}>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={exportPDF}
                    className="d-flex justify-content-center  p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="PDF"
                  >
                    <FaFilePdf size={16} />
                  </Button>
                  <Button
                    color="secondary"
                    onClick={exportExcel}
                    className="d-flex justify-content-center  p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="EXCEL"
                  >
                    <FaFileExcel size={16} />
                  </Button>
                </div>
              </Col>

              <Col xl={5}></Col>

              <Col xl={5}>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0">From:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date: Date | null) => {
                        setFromDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>

                  <label className="mb-0">To:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        setToDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar mt-3">
              <DataTable
                data={columnsData}
                columns={getCashFlowColumns()}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                sortServer
                onSort={handleSort}
                responsive
                progressPending={loading}
                progressComponent={
                  <div className="p-5">
                    <Spinner color="primary" />
                  </div>
                }
                conditionalRowStyles={conditionalRowStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      <ToastContainer />
    </>
  );
}
